import React, { useState, useEffect } from "react";
import { X, Save, Download } from "lucide-react";
import { Button } from "../../../../../foundation/altzero_base/base/components/ui/button";
import { useToast } from "../../../../../foundation/altzero_base/base/hooks/use-toast";

interface HTMLTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateName: string;
  templateFilePath?: string;
  templateId?: string;
}

export function HTMLTemplateModal({
  isOpen,
  onClose,
  templateName,
  templateFilePath,
  templateId,
}: HTMLTemplateModalProps) {
  const { toast } = useToast();
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load HTML template when modal opens
  useEffect(() => {
    if (isOpen && templateFilePath) {
      loadHTMLTemplate();
    }
  }, [isOpen, templateFilePath]);

  const loadHTMLTemplate = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(templateFilePath!);
      if (!response.ok) {
        throw new Error("Failed to load template");
      }
      const html = await response.text();
      setHtmlContent(html);
    } catch (error) {
      console.error("Error loading HTML template:", error);
      toast({
        title: "Error",
        description: "Failed to load template. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Get the edited HTML content from the iframe
      const iframe = document.getElementById('template-iframe') as HTMLIFrameElement;
      if (iframe && iframe.contentDocument) {
        const editedHTML = iframe.contentDocument.documentElement.outerHTML;

        // TODO: Save to Supabase storage/database
        console.log("Saving template:", {
          templateId,
          templateName,
          content: editedHTML
        });

        toast({
          title: "Success",
          description: "Template saved successfully!",
        });
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error",
        description: "Failed to save template. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => {
    const iframe = document.getElementById('template-iframe') as HTMLIFrameElement;
    if (iframe && iframe.contentDocument) {
      const editedHTML = iframe.contentDocument.documentElement.outerHTML;
      const blob = new Blob([editedHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateName.replace(/\s+/g, '-').toLowerCase()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Template downloaded successfully!",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0 bg-gray-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{templateName}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Click on any text to edit • All changes are editable
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Modal Content - Iframe Rendering */}
        <div className="flex-1 overflow-hidden bg-white">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading template...</p>
              </div>
            </div>
          ) : htmlContent ? (
            <iframe
              id="template-iframe"
              srcDoc={htmlContent}
              className="w-full h-[60vh] border-none"
              title={templateName}
              sandbox="allow-same-origin allow-scripts"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">No template content available</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={handleDownload}
            variant="outline"
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Download HTML
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
          >
            <Save className="h-4 w-4" />
            {isSaving ? "Saving..." : "Save Template"}
          </Button>
        </div>
      </div>
    </div>
  );
}
